'use client';

import { Urls } from '@/data/constants/urls';
import { OtnGame } from '@/data/models/ot_nationals/otn_game';
import { OtnGameOtnTeam } from '@/data/models/ot_nationals/otn_game_otn_team';
import { OtnGameOtnTeamOtnPlayer } from '@/data/models/ot_nationals/otn_game_otn_team_otn_player';
import { useLiveBoxStats } from '@/hooks/useLiveStats';
import { cn, toNum } from '@/lib/utils';
import Image from 'next/image';
import { useState } from 'react';
import StatTable, { StatTableRow } from './StatTable';
import TeamComparison from './TeamComparison';

type GameStatisticsProps = {
	game: OtnGame;
	playersStats?: OtnGameOtnTeamOtnPlayer[];
	/** Keep team and player rows live from the socket. Off unless the game's switch is on. */
	live?: boolean;
	/** The page is a ?hidden=true preview, so a re-fetch must ask for the hidden game too. */
	preview?: boolean;
};

/** A sack total that may be fractional (live stats) or the legacy integer (Genius). */
const sacksOf = (row: { defensive_sacks?: number; defensive_sacks_total?: number }) =>
	toNum(row.defensive_sacks_total) || toNum(row.defensive_sacks);

/**
 * Total tackles as official high school box scores count them: solo + ½ assisted (TECHENG-758).
 * Genius-era rows have no solo/assisted split, so they keep their stored total unchanged.
 */
const tacklesOf = (row: {
	defensive_tackles?: number;
	defensive_tackles_solo?: number;
	defensive_tackles_assisted?: number;
}) => {
	const solo = toNum(row.defensive_tackles_solo);
	const assisted = toNum(row.defensive_tackles_assisted);
	return solo || assisted ? solo + assisted / 2 : toNum(row.defensive_tackles);
};

const twoPointLabel = (row: { two_point_conversions_made?: number; two_point_conversions_attempted?: number }) =>
	toNum(row.two_point_conversions_attempted)
		? `${toNum(row.two_point_conversions_made)}/${toNum(row.two_point_conversions_attempted)}`
		: '';

const EMPTY_PLAYERS: OtnGameOtnTeamOtnPlayer[] = [];

const GameStatistics = ({
	game,
	playersStats: initialPlayersStats = EMPTY_PLAYERS,
	live = false,
	preview = false,
}: GameStatisticsProps) => {
	const { teams, players: playersStats } = useLiveBoxStats(
		game.id,
		(game?.otn_games_otn_teams ?? []) as OtnGameOtnTeam[],
		initialPlayersStats,
		live,
		preview,
	);
	const homeTeamGame = teams.find((t) => t.is_home_team);
	const homeTeam = homeTeamGame?.otn_team;
	const awayTeamGame = teams.find((t) => !t.is_home_team);
	const awayTeam = awayTeamGame?.otn_team;

	// default to home team if we have one
	const [activeTeamId, setActiveTeamId] = useState<string | null>(homeTeamGame?.id ?? null);

	const activeTeamGame = teams.find((t) => t.id === activeTeamId) ?? homeTeamGame ?? awayTeamGame ?? null;

	if (!activeTeamGame) return null;

	const getPlayersForTeam = (teamGame: OtnGameOtnTeam): OtnGameOtnTeamOtnPlayer[] =>
		playersStats.filter((p) => p.otn_game_id === teamGame.otn_game_id && p.otn_team_id === teamGame.otn_team_id);

	// ----- PASSING -----
	const buildPassingRowsForTeam = (teamGame: OtnGameOtnTeam): StatTableRow[] => {
		const playersForTeam = getPlayersForTeam(teamGame).filter((p) => {
			const att = toNum(p.passing_attempts);
			const cmp = toNum(p.passing_completions);
			const yds = toNum(p.passing_yards);
			const td = toNum(p.passing_touchdowns);
			const int = toNum(p.passing_interceptions);

			return att !== 0 || cmp !== 0 || yds !== 0 || td !== 0 || int !== 0;
		});

		const playerRows: StatTableRow[] = playersForTeam.map((p) => {
			const jersey = p.otn_player?.jersey_number ?? '';
			const name = p.otn_player?.full_name ?? '';

			return {
				id: `player-${teamGame.id}-${p.id}-passing`,
				label: name,
				jersey,
				att: toNum(p.passing_attempts),
				cmp: toNum(p.passing_completions),
				yds: toNum(p.passing_yards),
				td: toNum(p.passing_touchdowns),
				int: toNum(p.passing_interceptions),
				lng: toNum(p.passing_long),
			};
		});

		const teamRow: StatTableRow = {
			id: `team-${teamGame.id}-passing`,
			label: 'TEAM',
			jersey: '',
			att: toNum(teamGame.passing_attempts),
			cmp: toNum(teamGame.passing_completions),
			yds: toNum(teamGame.passing_yards),
			td: toNum(teamGame.passing_touchdowns),
			int: toNum(teamGame.passing_interceptions),
			lng: toNum(teamGame.passing_long),
		};

		return [...playerRows, teamRow];
	};

	// ----- RUSHING -----
	const buildRushingRowsForTeam = (teamGame: OtnGameOtnTeam): StatTableRow[] => {
		const playersForTeam = getPlayersForTeam(teamGame).filter((p) => {
			const att = toNum(p.rushing_attempts);
			const yds = toNum(p.rushing_yards);
			const td = toNum(p.rushing_touchdowns);

			return att !== 0 || yds !== 0 || td !== 0;
		});

		const playerRows: StatTableRow[] = playersForTeam.map((p) => {
			const jersey = p.otn_player?.jersey_number ?? '';
			const name = p.otn_player?.full_name ?? '';

			return {
				id: `player-${teamGame.id}-${p.id}-rushing`,
				label: name,
				jersey,
				att: toNum(p.rushing_attempts),
				yds: toNum(p.rushing_yards),
				td: toNum(p.rushing_touchdowns),
				lng: toNum(p.rushing_long),
				fum: toNum(p.fumbles),
				lost: toNum(p.fumbles_lost),
			};
		});

		const teamRow: StatTableRow = {
			id: `team-${teamGame.id}-rushing`,
			label: 'TEAM',
			jersey: '',
			att: toNum(teamGame.rushing_attempts),
			yds: toNum(teamGame.rushing_yards),
			td: toNum(teamGame.rushing_touchdowns),
			lng: toNum(teamGame.rushing_long),
			fum: toNum(teamGame.fumbles),
			lost: toNum(teamGame.fumbles_lost),
		};

		return [...playerRows, teamRow];
	};

	// ----- RECEIVING -----
	const buildReceivingRowsForTeam = (teamGame: OtnGameOtnTeam): StatTableRow[] => {
		const playersForTeam = getPlayersForTeam(teamGame).filter((p) => {
			const rec = toNum(p.receiving_receptions);
			const yds = toNum(p.receiving_yards);
			const td = toNum(p.receiving_touchdowns);

			return rec !== 0 || yds !== 0 || td !== 0;
		});

		const playerRows: StatTableRow[] = playersForTeam.map((p) => {
			const jersey = p.otn_player?.jersey_number ?? '';
			const name = p.otn_player?.full_name ?? '';

			return {
				id: `player-${teamGame.id}-${p.id}-receiving`,
				label: name,
				jersey,
				rec: toNum(p.receiving_receptions),
				yds: toNum(p.receiving_yards),
				td: toNum(p.receiving_touchdowns),
				lng: toNum(p.receiving_long),
			};
		});

		const teamRow: StatTableRow = {
			id: `team-${teamGame.id}-receiving`,
			label: 'TEAM',
			jersey: '',
			rec: toNum(teamGame.receiving_receptions),
			yds: toNum(teamGame.receiving_yards),
			td: toNum(teamGame.receiving_touchdowns),
			lng: toNum(teamGame.receiving_long),
		};

		return [...playerRows, teamRow];
	};

	// ----- DEFENSE -----
	const buildDefenseRowsForTeam = (teamGame: OtnGameOtnTeam): StatTableRow[] => {
		const playersForTeam = getPlayersForTeam(teamGame).filter((p) => {
			const tkl = tacklesOf(p);
			const sack = sacksOf(p);
			const int = toNum(p.defensive_interceptions);
			const td = toNum(p.defensive_touchdowns);
			const pd = toNum(p.defensive_pass_breakups);
			const ff = toNum(p.defensive_forced_fumbles);
			const fr = toNum(p.defensive_fumble_recoveries);

			return tkl !== 0 || sack !== 0 || int !== 0 || td !== 0 || pd !== 0 || ff !== 0 || fr !== 0;
		});

		const playerRows: StatTableRow[] = playersForTeam.map((p) => {
			const jersey = p.otn_player?.jersey_number ?? '';
			const name = p.otn_player?.full_name ?? '';

			return {
				id: `player-${teamGame.id}-${p.id}-defense`,
				label: name,
				jersey,
				tkl: tacklesOf(p),
				sack: sacksOf(p),
				int: toNum(p.defensive_interceptions),
				td: toNum(p.defensive_touchdowns),
				pd: toNum(p.defensive_pass_breakups),
				solo: toNum(p.defensive_tackles_solo),
				ast: toNum(p.defensive_tackles_assisted),
				tfl: toNum(p.defensive_tackles_for_loss),
				ff: toNum(p.defensive_forced_fumbles),
				fr: toNum(p.defensive_fumble_recoveries),
			};
		});

		const teamRow: StatTableRow = {
			id: `team-${teamGame.id}-defense`,
			label: 'TEAM',
			jersey: '',
			tkl: tacklesOf(teamGame),
			sack: sacksOf(teamGame),
			int: toNum(teamGame.defensive_interceptions),
			td: toNum(teamGame.defensive_touchdowns),
			pd: toNum(teamGame.defensive_pass_breakups),
			solo: toNum(teamGame.defensive_tackles_solo),
			ast: toNum(teamGame.defensive_tackles_assisted),
			tfl: toNum(teamGame.defensive_tackles_for_loss),
			ff: toNum(teamGame.defensive_forced_fumbles),
			fr: toNum(teamGame.defensive_fumble_recoveries),
		};

		return [...playerRows, teamRow];
	};

	// ----- KICKING -----
	const buildKickingRowsForTeam = (teamGame: OtnGameOtnTeam): StatTableRow[] => {
		const playersForTeam = getPlayersForTeam(teamGame).filter((p) => {
			const fgm = toNum(p.field_goals_made);
			const fga = toNum(p.field_goals_attempted);
			const xpm = toNum(p.extra_points_kicked_made);
			const xpa = toNum(p.extra_points_kicked_attempted);

			return fgm !== 0 || fga !== 0 || xpm !== 0 || xpa !== 0;
		});

		const playerRows: StatTableRow[] = playersForTeam.map((p) => {
			const jersey = p.otn_player?.jersey_number ?? '';
			const name = p.otn_player?.full_name ?? '';

			return {
				id: `player-${teamGame.id}-${p.id}-kicking`,
				label: name,
				jersey,
				fgm: toNum(p.field_goals_made),
				fga: toNum(p.field_goals_attempted),
				xpm: toNum(p.extra_points_kicked_made),
				xpa: toNum(p.extra_points_kicked_attempted),
				lng: toNum(p.field_goals_long),
			};
		});

		const teamRow: StatTableRow = {
			id: `team-${teamGame.id}-kicking`,
			label: 'TEAM',
			jersey: '',
			fgm: toNum(teamGame.field_goals_made),
			fga: toNum(teamGame.field_goals_attempted),
			xpm: toNum(teamGame.extra_points_kicked_made),
			xpa: toNum(teamGame.extra_points_kicked_attempted),
			lng: toNum(teamGame.field_goals_long),
			two: twoPointLabel(teamGame),
			twoAtt: toNum(teamGame.two_point_conversions_attempted),
		};

		return [...playerRows, teamRow];
	};

	// ----- PUNTING (live stats only; hidden when nobody punted) -----
	const buildPuntingRowsForTeam = (teamGame: OtnGameOtnTeam): StatTableRow[] => {
		if (!toNum(teamGame.punts)) return [];
		const toRow = (
			id: string,
			label: string,
			jersey: string | number,
			row: OtnGameOtnTeam | OtnGameOtnTeamOtnPlayer,
		) => {
			const no = toNum(row.punts);
			const yds = toNum(row.punting_yards);
			return {
				id,
				label,
				jersey,
				no,
				yds,
				avg: no ? (yds / no).toFixed(1) : '0.0',
				lng: toNum(row.punting_long),
				in20: toNum(row.punts_inside_20),
			};
		};
		const playerRows = getPlayersForTeam(teamGame)
			.filter((p) => toNum(p.punts) !== 0)
			.map((p) =>
				toRow(
					`player-${teamGame.id}-${p.id}-punting`,
					p.otn_player?.full_name ?? '',
					p.otn_player?.jersey_number ?? '',
					p,
				),
			);
		return [...playerRows, toRow(`team-${teamGame.id}-punting`, 'TEAM', '', teamGame)];
	};

	// ----- RETURNS (live stats only; hidden when there were no returns) -----
	const buildReturnRowsForTeam = (teamGame: OtnGameOtnTeam): StatTableRow[] => {
		if (!toNum(teamGame.kick_returns) && !toNum(teamGame.punt_returns)) return [];
		const toRow = (
			id: string,
			label: string,
			jersey: string | number,
			row: OtnGameOtnTeam | OtnGameOtnTeamOtnPlayer,
		) => ({
			id,
			label,
			jersey,
			kr: toNum(row.kick_returns),
			krYds: toNum(row.kick_return_yards),
			krTd: toNum(row.kick_return_touchdowns),
			pr: toNum(row.punt_returns),
			prYds: toNum(row.punt_return_yards),
			prTd: toNum(row.punt_return_touchdowns),
		});
		const playerRows = getPlayersForTeam(teamGame)
			.filter((p) => toNum(p.kick_returns) !== 0 || toNum(p.punt_returns) !== 0)
			.map((p) =>
				toRow(
					`player-${teamGame.id}-${p.id}-returns`,
					p.otn_player?.full_name ?? '',
					p.otn_player?.jersey_number ?? '',
					p,
				),
			);
		return [...playerRows, toRow(`team-${teamGame.id}-returns`, 'TEAM', '', teamGame)];
	};

	const passingRows = buildPassingRowsForTeam(activeTeamGame);
	const rushingRows = buildRushingRowsForTeam(activeTeamGame);
	const receivingRows = buildReceivingRowsForTeam(activeTeamGame);
	const defenseRows = buildDefenseRowsForTeam(activeTeamGame);
	const kickingRows = buildKickingRowsForTeam(activeTeamGame);
	const puntingRows = buildPuntingRowsForTeam(activeTeamGame);
	const returnRows = buildReturnRowsForTeam(activeTeamGame);

	const homeTeamLogo = homeTeam?.logo_path || '';
	const awayTeamLogo = awayTeam?.logo_path || '';

	return (
		<section className="flex flex-col items-center justify-center gap-4">
			<TeamComparison homeTeamGame={homeTeamGame} awayTeamGame={awayTeamGame} />
			<div className="mx-auto flex w-full max-w-[461px] min-w-0">
				{homeTeamGame && (
					<button
						type="button"
						onClick={() => setActiveTeamId(homeTeamGame.id)}
						className={cn(
							`flex-1 rounded-tl-[16px] rounded-bl-[16px] px-4 py-2 text-[13px] ${
								activeTeamGame.id === homeTeamGame.id ? 'bg-white text-black' : 'text-otnGray bg-[#4C4C4C]'
							}`,
						)}
					>
						<div className="flex items-center justify-center gap-2">
							<Image src={`${Urls.OvertimeImagesBaseUrl}${homeTeamLogo}`} alt="Home Team Logo" width={30} height={30} />
							<div className="text-[16px] leading-[130%] font-extrabold">
								<p className="hidden md:block">{homeTeam?.name}</p>
								<p className="uppercase md:hidden">{homeTeam?.team_code}</p>
							</div>
						</div>
					</button>
				)}
				{awayTeamGame && (
					<button
						type="button"
						onClick={() => setActiveTeamId(awayTeamGame.id)}
						className={cn(
							`flex-1 rounded-tr-[16px] rounded-br-[16px] px-4 py-2 text-[13px] ${
								activeTeamGame.id === awayTeamGame.id ? 'bg-white text-black' : 'text-otnGray bg-[#4C4C4C]'
							}`,
						)}
					>
						<div className="flex items-center justify-center gap-2">
							<Image src={`${Urls.OvertimeImagesBaseUrl}${awayTeamLogo}`} alt="Away Team Logo" width={30} height={30} />
							<div className="text-[16px] leading-[130%] font-extrabold">
								<p className="hidden md:block">{awayTeam?.name}</p>
								<p className="uppercase md:hidden">{awayTeam?.team_code}</p>
							</div>
						</div>
					</button>
				)}
			</div>

			<StatTable title="PASSING" category="passing" rows={passingRows} />
			<StatTable title="RUSHING" category="rushing" rows={rushingRows} />
			<StatTable title="RECEIVING" category="receiving" rows={receivingRows} />
			<StatTable title="DEFENSE" category="defense" rows={defenseRows} />
			<StatTable title="KICKING" category="kicking" rows={kickingRows} />
			<StatTable title="PUNTING" category="punting" rows={puntingRows} />
			<StatTable title="RETURNS" category="returns" rows={returnRows} />
		</section>
	);
};

export default GameStatistics;
